/**
 * Module dependencies.
 */

import { ArrowLeft } from 'lucide-react';
import { GREEN_BRIGHT, GREEN_DARK, GREEN_MID } from 'app/components/pages/landing/brand';
import { appRoutes } from 'app/routes';
import { useNavigate } from 'react-router-dom';
import React from 'react';

/**
 * Green radial nebula glow.
 */

function Glow({
  blur,
  className,
  size,
  tint
}: {
  blur: number;
  className: string;
  size: number;
  tint: string;
}): JSX.Element {
  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute rounded-full ${className}`}
      style={{ backgroundImage: tint, filter: `blur(${blur}px)`, height: size, opacity: 0.95, width: size }}
    />
  );
}

/**
 * Export `AuthShell` — background + centered glass card + back-to-home link.
 */

export function AuthShell({ children }: { children: React.ReactNode }): JSX.Element {
  const navigate = useNavigate();

  return (
    <div
      className={
        "relative isolate grid min-h-screen place-items-center overflow-hidden px-6 py-10 font-['Roboto'] text-[#0A0A0B]"
      }
      style={{ backgroundColor: '#F4F4F6' }}
    >
      {/* Decorative background */}
      <div
        aria-hidden
        className={'pointer-events-none absolute inset-0 -z-10'}
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(10,10,11,0.16) 1px, transparent 1px)',
          backgroundSize: '26px 26px',
          maskImage: 'radial-gradient(80% 60% at 50% 40%, #000 30%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(80% 60% at 50% 40%, #000 30%, transparent 100%)'
        }}
      />

      <Glow
        blur={100}
        className={'-left-40 -top-40 -z-10'}
        size={620}
        tint={'radial-gradient(circle at 40% 40%, rgba(34,165,68,0.34), transparent 70%)'}
      />

      <Glow
        blur={110}
        className={'-bottom-44 -right-44 -z-10'}
        size={640}
        tint={'radial-gradient(circle, rgba(27,117,43,0.24), transparent 70%)'}
      />

      <div className={'relative z-10 w-full max-w-md'}>
        <div
          className={'rounded-3xl border border-white/60 p-8 text-center backdrop-blur-xl sm:p-10'}
          style={{
            backgroundImage: 'linear-gradient(160deg, rgba(255,255,255,0.82), rgba(255,255,255,0.56))',
            boxShadow: '0 40px 90px -40px rgba(10,10,11,0.4), inset 0 1px 0 0 rgba(255,255,255,0.9)'
          }}
        >
          {children}
        </div>

        <button
          className={
            'mx-auto mt-6 flex items-center gap-1.5 text-sm text-neutral-500 transition-colors hover:text-[#1B752B]'
          }
          onClick={() => navigate(appRoutes.home)}
          type={'button'}
        >
          <ArrowLeft className={'h-4 w-4'} />

          {'Back to home'}
        </button>
      </div>
    </div>
  );
}

/**
 * Export `AuthField` — labeled input.
 */

export function AuthField({
  autoComplete,
  label,
  onChange,
  type,
  value
}: {
  autoComplete: string;
  label: string;
  onChange: (value: string) => void;
  type: string;
  value: string;
}): JSX.Element {
  return (
    <label className={'block text-left'}>
      <span className={'mb-1.5 block text-xs font-medium text-neutral-600'}>{label}</span>

      <input
        autoComplete={autoComplete}
        className={
          'w-full rounded-lg border border-neutral-200 bg-white px-3.5 py-2.5 text-sm text-neutral-800 outline-none transition-colors placeholder:text-neutral-400 focus:border-[#1B752B] focus:ring-2 focus:ring-[#1B752B]/15'
        }
        onChange={event => onChange(event.target.value)}
        type={type}
        value={value}
      />
    </label>
  );
}

/**
 * Export `AuthSubmit` — full-width green gradient-sweep button.
 */

export function AuthSubmit({ children, disabled }: { children: React.ReactNode; disabled?: boolean }): JSX.Element {
  return (
    <button
      className={
        'h-11 w-full rounded-lg bg-[length:200%_auto] bg-left text-sm font-medium text-white transition-[background-position] duration-500 ease-out hover:bg-right disabled:cursor-not-allowed disabled:opacity-60'
      }
      disabled={disabled}
      style={{ backgroundImage: `linear-gradient(120deg, ${GREEN_DARK}, ${GREEN_MID}, ${GREEN_BRIGHT})` }}
      type={'submit'}
    >
      {children}
    </button>
  );
}
